import { useCallback } from 'react';
import { useSetAtom } from 'jotai';

import { authModalAtom, AuthReason } from '../atoms';
import { Context, StateAuth, useAuth } from '../context/auth';
import { saveAuthRedirect } from '../helpers';

interface RequireAuthOptions {
  // Page to come back to after the login (ex: /feed?video=<id>).
  redirect?: string;
  // Name shown in the title of the popup (ex: the creator to follow).
  username?: string;
}

// An action reserved to the connected users: done at once when connected, otherwise the
// login popup opens with a title that tells why. A new locked action = a reason + its texts.
const useRequireAuth = () => {
  const { user } = useAuth() as Context;
  const setAuthModal = useSetAtom(authModalAtom);
  const isGuest = user.isConnected !== StateAuth.CONNECTED;

  const openAuthModal = useCallback((reason: AuthReason, options: RequireAuthOptions = {}) => {
    saveAuthRedirect(options.redirect || `${window.location.pathname}${window.location.search}`);
    let view: 'login' | 'signup' | 'signupInvite' = 'login';
    if (reason === 'signup') {
      view = 'signup';
    } else if (reason === 'signupInvite') {
      view = 'signupInvite';
    }
    setAuthModal({ reason, view, username: options.username });
  }, [setAuthModal]);

  const requireAuth = useCallback((reason: AuthReason, action: () => void, options?: RequireAuthOptions) => {
    if (!isGuest) {
      action();
      return;
    }
    openAuthModal(reason, options);
  }, [isGuest, openAuthModal]);

  return { isGuest, openAuthModal, requireAuth };
};

export default useRequireAuth;
